import { Result } from '@/classes/result';
import { Api } from '@/enums/api';
import router from '@/router';
import { useGlobalStore } from '@/stores/global';
import type { RequestParameters } from '@/types/api';
import toast from '@/utils/toast';
import axios, { type AxiosRequestConfig } from 'axios';

// TODO: dont throw error to make it possible in the client to handle the error.
// status code != 200 range will be handled as an error immediately. Hereby removing the need to
// check if  the status code is in the 200 range in the client.
export async function callApi<T extends object = Result>(requestParameters: RequestParameters): Promise<T> {
    const globalStore = useGlobalStore();
    globalStore.isLoading = true;

    try {
        const { method, endpoint, body = null, jwtTokenRequired = true, api = Api.Incontrol } = requestParameters;
        const jwtToken = localStorage.getItem('token');

        // Prevent calls made after logging out
        if (jwtTokenRequired && !jwtToken) {
            await router.push({ name: 'login' });
            return {} as T;
        }

        const contentType = !(body instanceof FormData) ? 'application/json' : null;
        const requestBody = body ? (body instanceof FormData ? body : JSON.stringify(body)) : undefined;

        const options: AxiosRequestConfig = {
            method,
            data: requestBody,
            ...getApiConfig(api, endpoint, jwtToken, jwtTokenRequired, contentType)
        };

        const response = await axios.request<T>(options);
        return response.data;
    } finally {
        globalStore.isLoading = false;
    }
}

function getApiConfig(
    api: Api,
    endpoint: string,
    jwtToken: string | null,
    jwtTokenRequired: boolean,
    contentType: string | null
): AxiosRequestConfig {
    const baseUrl = api === Api.Incontrol ? import.meta.env.VITE_API_URL : import.meta.env.VITE_API_V2_URL;

    return {
        url: `${baseUrl}/${endpoint}`,
        headers: {
            Authorization: jwtTokenRequired ? `Bearer ${jwtToken}` : '',
            ...(contentType !== null && { 'Content-Type': contentType })
        }
    };
}

export async function detectApiConnectionStatus(): Promise<void> {
    const globalStore = useGlobalStore();
    const response = await callApi({
        method: 'GET',
        endpoint: '',
        jwtTokenRequired: false
    });

    if (response.status === 200) {
        globalStore.isConnectionError = false;
        toast.success('Reconnected');
    } else {
        globalStore.isConnectionError = true;
        toast.error('NetworkError');
    }
}
